import { useRef } from "react";
import { IonIcon } from "@ionic/react";
import { trashOutline } from "ionicons/icons";
import { Haptics, ImpactStyle } from "@capacitor/haptics";

// Swipe a list row sideways the way mail apps do. The row follows the finger
// from the first horizontal pixel over a coloured field with an icon; the
// icon pops (with a light tap) once the swipe is far enough to count. Let go
// there and the action runs: a row that `leaves` (delete, close, reopen)
// slides out and folds shut first; one that stays (pin) springs back home
// first. Let go early and it springs back with a small overshoot. Vertical
// scrolling is untouched (touch-action: pan-y), and a swipe never counts as a
// tap on the row. The transform is written straight to the element while
// dragging, so nothing re-renders between frames.
//
//   <SwipeRow onDelete={fn}>                       a left swipe deletes (red, trash)
//   <SwipeRow left={{ icon, color, contrast, onCommit, leaves }} right={{ ... }}>
//
// A side with no action only rubber-bands.
export default function SwipeRow({ children, onDelete, left, right, threshold = 0.42, className = "", background = "var(--ion-card-background)" }) {
  const leftAction = left || (onDelete ? { icon: trashOutline, color: "var(--ion-color-danger)", contrast: "var(--ion-color-danger-contrast, #fff)", onCommit: onDelete, leaves: true } : null);
  const rightAction = right || null;
  const wrap = useRef(null);
  const content = useRef(null);
  const iconL = useRef(null);
  const iconR = useRef(null);
  const st = useRef({ active: false, decided: null, startX: 0, startY: 0, dx: 0, dir: null, armed: false, swiped: false });

  const setX = (x) => { if (content.current) content.current.style.transform = `translate3d(${x}px, 0, 0)`; };
  const setDir = (dir) => {
    const s = st.current;
    if (s.dir === dir) return;
    s.dir = dir;
    const cl = wrap.current?.classList;
    if (!cl) return;
    cl.toggle("swipe-left", dir === "left");
    cl.toggle("swipe-right", dir === "right");
  };
  const arm = (on) => {
    const s = st.current;
    if (s.armed === on) return;
    s.armed = on;
    const icon = s.dir === "right" ? iconR.current : iconL.current;
    if (icon) icon.style.transform = on ? "scale(1.3)" : "scale(1)";
    Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
  };
  const settle = () => {
    const cl = wrap.current?.classList;
    if (cl) { cl.remove("is-swiping"); cl.remove("swipe-left"); cl.remove("swipe-right"); }
    st.current.dir = null;
    [iconL.current, iconR.current].forEach((i) => { if (i) i.style.transform = "scale(1)"; });
  };

  const down = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    st.current = { active: true, decided: null, startX: e.clientX, startY: e.clientY, dx: 0, dir: null, armed: false, swiped: false };
    if (content.current) { content.current.getAnimations?.().forEach((a) => a.cancel()); content.current.style.transition = "none"; }
  };
  const move = (e) => {
    const s = st.current;
    if (!s.active) return;
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    if (!s.decided) {
      if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) { s.active = false; return; } // a scroll, not a swipe
      if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
        s.decided = "x";
        wrap.current?.classList.add("is-swiping"); // the row keeps its own colour under the finger (no pressed grey, no ripple)
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* fine without capture */ }
      } else return;
    }
    const w = wrap.current ? wrap.current.offsetWidth : 1;
    const action = dx < 0 ? leftAction : rightAction;
    const x = action ? dx : dx * 0.15; // a side without an action only rubber-bands
    s.dx = x;
    s.swiped = true;
    setDir(dx < 0 ? "left" : dx > 0 ? "right" : s.dir);
    setX(x);
    arm(!!action && Math.abs(x) >= Math.min(w * threshold, 150));
  };
  const finish = () => {
    const s = st.current;
    if (!s.active) return;
    s.active = false;
    setTimeout(() => { s.swiped = false; }, 0); // the click that follows this release is not a tap
    if (s.decided !== "x") return;
    const el = content.current, box = wrap.current;
    if (!el || !box) return;
    const action = s.dir === "right" ? rightAction : leftAction;
    const home = (then) => {
      // back home, with the little overshoot; the field stays painted under
      // the row until it is home
      setX(0);
      const over = s.dx < 0 ? 6 : -6;
      const back = el.animate(
        [{ transform: `translate3d(${s.dx}px, 0, 0)` }, { transform: `translate3d(${over}px, 0, 0)`, offset: 0.75 }, { transform: "translate3d(0, 0, 0)" }],
        { duration: 320, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
      );
      back.onfinish = () => { settle(); then?.(); };
      s.armed = false;
    };
    if (s.armed && action) {
      if (action.leaves === false) { home(() => action.onCommit?.()); return; }
      // out the way it was going, then fold shut, then gone
      const w = box.offsetWidth, h = box.offsetHeight;
      const out = s.dir === "right" ? w + 24 : -w - 24;
      const slide = el.animate(
        [{ transform: `translate3d(${s.dx}px, 0, 0)` }, { transform: `translate3d(${out}px, 0, 0)` }],
        { duration: 160, easing: "cubic-bezier(0.4, 0, 1, 1)", fill: "forwards" },
      );
      slide.onfinish = () => {
        const fold = box.animate(
          [{ height: `${h}px`, opacity: 1 }, { height: "0px", opacity: 0 }],
          { duration: 200, easing: "cubic-bezier(0.4, 0, 0.2, 1)", fill: "forwards" },
        );
        fold.onfinish = () => action.onCommit?.();
      };
    } else {
      home();
    }
  };

  const field = (side, action, ref) => action ? (
    // painted only while a swipe heads that way (see .swipe-row-back): at rest
    // it would sit under every row and flash through on a fast scroll
    <div aria-hidden="true" className={`swipe-row-back swipe-row-back-${side}`} style={{ position: "absolute", inset: 0, background: action.color, color: action.contrast || "#fff", display: "flex", alignItems: "center", justifyContent: side === "left" ? "flex-end" : "flex-start", paddingRight: side === "left" ? 24 : 0, paddingLeft: side === "right" ? 24 : 0 }}>
      <IonIcon ref={ref} icon={action.icon} style={{ fontSize: 24, transition: "transform 160ms cubic-bezier(0.22, 1, 0.36, 1)" }} />
    </div>
  ) : null;

  return (
    <div
      ref={wrap}
      className={`swipe-row ${className}`.trim()}
      style={{ position: "relative", overflow: "hidden", touchAction: "pan-y" }}
      onClickCapture={(e) => { if (st.current.swiped) { e.stopPropagation(); e.preventDefault(); } }}
    >
      {field("left", leftAction, iconL)}
      {field("right", rightAction, iconR)}
      <div
        ref={content}
        className="swipe-row-content"
        style={{ position: "relative", background }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={finish}
        onPointerCancel={finish}
      >
        {children}
      </div>
    </div>
  );
}
